import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useMode } from '../contexts/ModeContext';
import axios from 'axios';
import { apiBaseUrl } from '../config/runtime';
import { isNativeApp } from '../utils/appBridge';
import {
  MessageCircle, Folder, User, ClipboardList,
  LogOut, Bell
} from 'lucide-react';
import './NavBar.css';

// 일부 안드로이드 기기(레노버 ZUI 등)는 화면 최상단에 분할화면/플로팅윈도우
// 시스템 바를 앱 위에 겹쳐 그려서 그 영역의 터치를 앱보다 먼저 가로챈다.
// OS가 보고하는 표준 안전영역(safe-area-inset-top: 노치·상태바)에 더해,
// 네이티브 앱에서만 여유 버퍼를 추가로 확보해 상단 메뉴 버튼이 그 영역과
// 최대한 겹치지 않도록 한다. (기기별 오버레이 크기가 달라 100% 보장은 아님)
export const NAV_TOP_SAFE_PADDING = `calc(env(safe-area-inset-top, 0px) + ${isNativeApp() ? 28 : 0}px)`;

const NAV_STYLE = {
  nav: {
    display: 'flex' as const,
    justifyContent: 'space-between' as const,
    alignItems: 'center' as const,
    padding: `${NAV_TOP_SAFE_PADDING} 24px 8px`,
    minHeight: 56,
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    backgroundColor: '#000',
    position: 'sticky' as const,
    top: 0,
    zIndex: 1000,
    fontFamily: "-apple-system, 'Pretendard', sans-serif",
  },
  logo: {
    fontSize: 18,
    fontWeight: 800,
    color: '#fff',
    textDecoration: 'none',
    letterSpacing: '-0.5px',
  },
};

const navLink = (active: boolean) => ({
  display: 'flex' as const,
  alignItems: 'center' as const,
  gap: 5,
  padding: '6px 12px',
  textDecoration: 'none',
  borderRadius: 7,
  fontSize: 13,
  fontWeight: 500,
  color: active ? '#fff' : 'rgba(255,255,255,0.5)',
  backgroundColor: active ? 'rgba(255,255,255,0.1)' : 'transparent',
  transition: 'all 0.15s',
  whiteSpace: 'nowrap' as const,
});

const NavBar: React.FC = () => {
  const { user, logout } = useAuth();
  const { isClientMode, isFreelancerMode, switchMode } = useMode();
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [unreadNotiCount, setUnreadNotiCount] = useState(0);

  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!user) return;
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const response = await axios.get(apiBaseUrl('/chat/unread-count'), {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUnreadChatCount(response.data.unread_count || 0);
      } catch {
        setUnreadChatCount(0);
      }
    };

    const fetchNotiCount = async () => {
      if (!user) return;
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await axios.get(apiBaseUrl('/notifications/unread-count'), {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUnreadNotiCount(res.data.count || 0);
      } catch {
        setUnreadNotiCount(0);
      }
    };

    fetchUnreadCount();
    fetchNotiCount();
    const interval = setInterval(() => { fetchUnreadCount(); fetchNotiCount(); }, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const isActive = (path: string) => location.pathname === path;
  const handleLogout = () => { logout(); navigate('/login'); };

  const { t, i18n } = useTranslation();
  const toggleLang = () => {
    const next = i18n.language.startsWith('ko') ? 'en' : 'ko';
    console.log('[NavBar] 언어 전환:', next);
    i18n.changeLanguage(next);
  };
  const langBtn = (
    <button onClick={toggleLang} style={{
      padding: '5px 10px', borderRadius: 7, fontSize: 12, fontWeight: 700,
      background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.6)',
      border: '1px solid rgba(255,255,255,0.12)', cursor: 'pointer', fontFamily: 'inherit',
    }}>
      {i18n.language.startsWith('ko') ? 'EN' : '한국어'}
    </button>
  );

  /* 비로그인 */
  if (!user) {
    return (
      <nav style={NAV_STYLE.nav} className="nav-root">
        <Link to="/" style={NAV_STYLE.logo}>freematch</Link>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {langBtn}
          <Link to="/login" style={{
            padding: '7px 16px', fontSize: 14, fontWeight: 500,
            color: 'rgba(255,255,255,0.55)', textDecoration: 'none', borderRadius: 7,
            transition: 'color 0.15s',
          }}>
            {t('nav.login')}
          </Link>
          <Link to="/register" style={{
            padding: '7px 16px', fontSize: 14, fontWeight: 600,
            background: '#fff', color: '#000',
            textDecoration: 'none', borderRadius: 7,
            transition: 'opacity 0.15s',
          }}>
            {t('nav.getStarted')}
          </Link>
        </div>
      </nav>
    );
  }

  /* 로그인 상태 */
  return (
    <nav style={NAV_STYLE.nav} className="nav-root">
      {/* 로고 */}
      <Link to="/" style={NAV_STYLE.logo}>freematch</Link>

      {/* 중앙 메뉴 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>

        {/* 모드 전환 */}
        <div style={{
          display: 'flex',
          background: 'rgba(255,255,255,0.07)',
          borderRadius: 9, padding: 3, marginRight: 6,
          border: '1px solid rgba(255,255,255,0.08)',
        }}>
          <button
            onClick={() => switchMode('client')}
            style={{
              padding: '5px 12px', borderRadius: 7, border: 'none',
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
              background: isClientMode ? 'rgba(255,255,255,0.15)' : 'transparent',
              color: isClientMode ? '#fff' : 'rgba(255,255,255,0.4)',
              transition: 'all 0.15s',
            }}
          >
            {t('nav.client')}
          </button>
          <button
            onClick={() => switchMode('freelancer')}
            style={{
              padding: '5px 12px', borderRadius: 7, border: 'none',
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
              background: isFreelancerMode ? 'rgba(255,255,255,0.15)' : 'transparent',
              color: isFreelancerMode ? '#fff' : 'rgba(255,255,255,0.4)',
              transition: 'all 0.15s',
            }}
          >
            {t('nav.expert')}
          </button>
        </div>

        <Link to="/profile" style={navLink(isActive('/profile'))} title={t('nav.profile')}>
          <User size={14} /><span className="nav-label">{t('nav.profile')}</span>
        </Link>

        <Link
          to={isClientMode ? '/my-projects' : '/my-services'}
          style={navLink(isActive('/my-projects') || isActive('/my-services'))}
          title={isClientMode ? t('nav.myProjects') : t('nav.myServices')}
        >
          <Folder size={14} />
          <span className="nav-label">{isClientMode ? t('nav.myProjects') : t('nav.myServices')}</span>
        </Link>

        <Link to="/chat" style={{ ...navLink(isActive('/chat')), position: 'relative' }} title={t('nav.chat')}>
          <MessageCircle size={14} /><span className="nav-label">{t('nav.chat')}</span>
          {unreadChatCount > 0 && (
            <span style={{
              position: 'absolute', top: 2, right: 2,
              background: '#ef4444', color: '#fff',
              borderRadius: '50%', width: 15, height: 15,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 9, fontWeight: 700,
              border: '2px solid #000',
            }}>
              {unreadChatCount > 9 ? '9+' : unreadChatCount}
            </span>
          )}
        </Link>

        <Link to="/transactions" style={navLink(isActive('/transactions'))} title={t('nav.transactions')}>
          <ClipboardList size={14} /><span className="nav-label">{t('nav.transactions')}</span>
        </Link>

        <Link to="/notifications" style={{ ...navLink(isActive('/notifications')), position: 'relative' }} title={t('nav.notifications', '알림')}>
          <Bell size={14} />
          {unreadNotiCount > 0 && (
            <span style={{
              position: 'absolute', top: 2, right: 2,
              background: '#ef4444', color: '#fff',
              borderRadius: '50%', width: 15, height: 15,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 9, fontWeight: 700,
              border: '2px solid #000',
            }}>
              {unreadNotiCount > 9 ? '9+' : unreadNotiCount}
            </span>
          )}
        </Link>
      </div>

      {/* 유저 + 로그아웃 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {langBtn}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '6px 12px',
          background: 'rgba(255,255,255,0.07)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 7, fontSize: 13,
        }}>
          <span style={{
            width: 7, height: 7,
            background: isClientMode ? '#a78bfa' : '#34d399',
            borderRadius: '50%',
          }} />
          <span style={{
            fontWeight: 600, color: '#fff',
            maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis',
          }}>{user.nickname || user.name}</span>
          <span style={{
            fontSize: 11, color: 'rgba(255,255,255,0.4)',
            background: 'rgba(255,255,255,0.08)',
            padding: '1px 6px', borderRadius: 4,
          }}>
            {isClientMode ? t('nav.client') : t('nav.expert')}
          </span>
        </div>
        <button
          onClick={handleLogout}
          title={t('nav.logout')}
          style={{
            display: 'flex', alignItems: 'center', gap: 5,
            padding: '6px 12px',
            background: 'rgba(255,255,255,0.06)',
            color: 'rgba(255,255,255,0.5)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 7, fontSize: 13, fontWeight: 500, cursor: 'pointer',
            transition: 'all 0.15s',
            whiteSpace: 'nowrap',
          }}
          onMouseOver={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = '#fff'; }}
          onMouseOut={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; }}
        >
          <LogOut size={13} /><span className="nav-label">{t('nav.logout')}</span>
        </button>
      </div>
    </nav>
  );
};

export default NavBar;
